import { useForm } from 'react-hook-form';
import { FirebaseError } from 'firebase/app';
import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, Navigate } from 'react-router';
import { TotpMultiFactorGenerator, multiFactor } from 'firebase/auth';
import { captureException as sentryCaptureException } from '@sentry/react';
import { Button, Stack, Typography, Stepper, Step, StepLabel, Fade, Box } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { useAuth } from '../../../../hooks';
import { MFA_FACTOR_IDS } from '../../constants';
import { mfaColors, mfaPrimaryButtonStyles, mfaSecondaryButtonStyles } from '../styles';
import { USER_TYPES } from '../../../../utils/constants';
import { Loading } from '../../../../components/layout';
import { NavigateWithRedirect } from '../../../../Router/NavigateWithRedirect';
import { handleFirebaseError, isUserEnrolledInMFAMethod } from '../../utils';
import { AuthenticationWrapper } from '../../AuthenticationWrapper';
import { DEFAULT_OTP_INPUT_FIELD_VALUE, NUMBER_OF_OTP_INPUTS, parseOTPInputValue } from '../../components/OTPInput';
import { addSuccessful2FAToActivityLog } from '../addSuccessful2FAToActivityLog';
import { SetupPage, SetupPageWithoutProvider, useMFASetupPageContext, MFASetupPageContextProvider } from '../SetupPage';
import { VerificationStep } from './VerificationStep';
import { STEPS, verificationCodeSchema } from './constants';
import { SetupAuthenticatorStep } from './SetupAuthenticatorStep';
import { SecurityRequirementPage } from './SecurityRequirementPage';
import { AuthenticatorSetupGuideLink } from '../AuthenticatorSetupGuideLink.tsx';

/**
 * @param {Object} props
 * @param {number} props.activeStep
 * @param {import('firebase/auth').TotpSecret | null} props.secret
 * @param {string} props.qrCode
 * @param {() => void} props.onSubmit
 */
const ActiveStep = ({ activeStep, secret, qrCode, onSubmit }) => {
  if (activeStep === 0) {
    return <SetupAuthenticatorStep secret={secret} qrCode={qrCode} />;
  }

  return <VerificationStep onSubmit={onSubmit} />;
};

const ActiveStepActions = ({
  activeStep,
  setActiveStep,
  user,
  isEnrolling,
  handleNext,
  code,
  onBack,
  canSelectMethod,
}) => {
  if (activeStep === 0) {
    return (
      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={2} width="100%" justifyContent="center">
        {canSelectMethod ? (
          <Button onClick={onBack} disabled={isEnrolling} sx={{ flex: 1, ...mfaSecondaryButtonStyles }}>
            Back
          </Button>
        ) : (
          <SetupPage.CancelButton user={user} disabled={isEnrolling} sx={{ flex: 1 }} />
        )}
        <Button onClick={handleNext} sx={{ flex: 1, minWidth: '120px', ...mfaPrimaryButtonStyles }}>
          Next
        </Button>
      </Stack>
    );
  }

  return (
    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={2} width="100%" justifyContent="center">
      <Button onClick={() => setActiveStep(0)} sx={{ flex: 1, ...mfaSecondaryButtonStyles }}>
        Back
      </Button>
      <Button
        type="submit"
        disabled={parseOTPInputValue(code).length !== NUMBER_OF_OTP_INPUTS || isEnrolling}
        sx={{ flex: 1, ...mfaPrimaryButtonStyles }}
      >
        {isEnrolling ? 'Setting up...' : 'Verify and Enable'}
      </Button>
    </Stack>
  );
};

const HelpLinks = () => (
  <Stack sx={{ textAlign: 'center', pt: 2, color: mfaColors.textMuted }}>
    <AuthenticatorSetupGuideLink />
  </Stack>
);

const MethodSelection = ({ onSelectTOTP, onSelectSMS }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} width="100%" sx={{ mt: 2 }}>
    <Box
      onClick={onSelectTOTP}
      sx={{
        flex: 1,
        border: `1px solid ${mfaColors.border}`,
        borderRadius: 2,
        p: 3,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.2s',
        backgroundColor: '#fff',
        '&:hover': {
          borderColor: mfaColors.primary,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: '#e0f2fe',
          color: mfaColors.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <SecurityIcon />
      </Box>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Authenticator App
      </Typography>
      <Typography variant="body2" color={mfaColors.textMuted}>
        Use an app like Google Authenticator or Authy to generate secure codes.
      </Typography>
    </Box>

    <Box
      onClick={onSelectSMS}
      sx={{
        flex: 1,
        border: `1px solid ${mfaColors.border}`,
        borderRadius: 2,
        p: 3,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.2s',
        backgroundColor: '#fff',
        '&:hover': {
          borderColor: mfaColors.primary,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: '#ede9fe',
          color: '#8b5cf6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <SmsOutlinedIcon />
      </Box>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Text Message (SMS)
      </Typography>
      <Typography variant="body2" color={mfaColors.textMuted}>
        Receive a verification code via text message to your mobile phone.
      </Typography>
    </Box>
  </Stack>
);

const TOTPSetupPageContent = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { reauthenticate } = useMFASetupPageContext();

  const [secret, setSecret] = useState(/** @type {import('firebase/auth').TotpSecret | null} */ (null));
  const [qrCode, setQrCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [showInformationPage, setShowInformationPage] = useState(
    !!location.state?.isAutomatedRedirect && !location.state?.skipInfoPage,
  );
  const canSelectMethod = user
    ? userType === USER_TYPES.PATIENT &&
      !isUserEnrolledInMFAMethod({ factorId: MFA_FACTOR_IDS.PHONE, multiFactorUser: multiFactor(user) })
    : false;
  const [methodSelected, setMethodSelected] = useState(!canSelectMethod);

  const form = useForm({
    resolver: zodResolver(verificationCodeSchema),
    defaultValues: {
      verificationCode: DEFAULT_OTP_INPUT_FIELD_VALUE,
    },
  });

  const {
    setError,
    watch,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    const generateSecret = async () => {
      if (!user) {
        return;
      }

      if (!methodSelected) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const multiFactorUser = multiFactor(user);

        const userAlreadyHasTOTP = isUserEnrolledInMFAMethod({ factorId: MFA_FACTOR_IDS.TOTP, multiFactorUser });

        if (userAlreadyHasTOTP) {
          navigate('/dashboard', { replace: true });
          return;
        }

        // If non-patient user has phone MFA, unenroll them (patients can keep both methods)
        const hasPhoneMFA = isUserEnrolledInMFAMethod({ factorId: MFA_FACTOR_IDS.PHONE, multiFactorUser });
        if (hasPhoneMFA && userType !== USER_TYPES.PATIENT) {
          const phoneFactor = multiFactorUser.enrolledFactors.find(
            (factor) => factor.factorId === MFA_FACTOR_IDS.PHONE,
          );
          if (phoneFactor) {
            await multiFactorUser.unenroll(phoneFactor);
          }
        }

        const multiFactorSession = await multiFactorUser.getSession();

        const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
        setSecret(totpSecret);

        const totpUri = totpSecret.generateQrCodeUrl(
          user.email || 'user',
          import.meta.env.VITE_APP_NAME || 'Candor Medical',
        );

        setQrCode(totpUri);
      } catch (error) {
        if (error instanceof FirebaseError) {
          handleFirebaseError({ error, setError, handleRequiresRecentLogin: reauthenticate });
          return;
        }

        setError('root', {
          message: 'Failed to generate TOTP secret. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    generateSecret();
  }, [user, userType, navigate, setError, reauthenticate, methodSelected]);

  /**
   * Handles the verification of the TOTP code
   * @param {import('./constants').TOTPVerificationFormType} data - The verification code data
   */
  const onSubmit = async (data) => {
    if (!secret || !user) {
      return;
    }

    const { verificationCode, displayName } = data;

    try {
      const parsedVerificationCode = parseOTPInputValue(verificationCode);
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, parsedVerificationCode);

      await multiFactor(user).enroll(multiFactorAssertion, displayName?.trim() || 'Authenticator');

      await addSuccessful2FAToActivityLog({ userId: user.uid, factorId: MFA_FACTOR_IDS.TOTP });

      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get('redirect');

      if (redirect) {
        searchParams.delete('redirect');
        navigate(`${redirect}?${searchParams.toString()}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        handleFirebaseError({
          error,
          setError,
          handleRequiresRecentLogin: reauthenticate,
        });

        return;
      }

      const sentryIssueId = sentryCaptureException(error, {
        level: 'error',
        extra: {
          issueIn: 'TOTPSetupPage:handleVerification',
          data,
        },
      });

      setError('root', {
        type: 'sentry',
        message: `Failed to enroll in TOTP. Please try again. Id: ${sentryIssueId}`,
      });
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  if (!user) {
    return null;
  }

  if (isLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (showInformationPage) {
    return <SecurityRequirementPage onContinue={() => setShowInformationPage(false)} />;
  }

  const code = watch('verificationCode');

  if (!methodSelected) {
    return (
      <SetupPageWithoutProvider
        title="Set Up Two-Factor Authentication"
        subtitle="Choose how you'd like to protect your account. We recommend using an Authenticator App for superior security."
        form={form}
        onSubmit={onSubmit}
      >
        <Fade in>
          <Stack spacing={4} width="100%">
            <MethodSelection
              onSelectTOTP={() => setMethodSelected(true)}
              onSelectSMS={() => navigate('/mfa-setup/phone', { state: location.state })}
            />

            <AuthenticationWrapper.Actions style={{ width: '100%', justifyContent: 'center' }}>
              {location.state?.isAutomatedRedirect ? (
                <Button onClick={() => setShowInformationPage(true)} sx={{ flex: 1, ...mfaSecondaryButtonStyles }}>
                  Back
                </Button>
              ) : (
                <SetupPage.CancelButton user={user} />
              )}
            </AuthenticationWrapper.Actions>
          </Stack>
        </Fade>
      </SetupPageWithoutProvider>
    );
  }

  return (
    <SetupPageWithoutProvider
      title="Set Up Two-Factor Authentication"
      subtitle="Authenticator apps are easy to use and provide superior account security. Follow the prompts below to complete your set up and continue to Candor."
      form={form}
      onSubmit={onSubmit}
    >
      <Fade in>
        <Stack spacing={4} width="100%">
          <Stepper activeStep={activeStep} sx={{ maxWidth: '400px', alignSelf: 'center' }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <ActiveStep qrCode={qrCode} secret={secret} activeStep={activeStep} onSubmit={form.handleSubmit(onSubmit)} />

          <AuthenticationWrapper.Actions style={{ width: '100%' }}>
            <ActiveStepActions
              user={user}
              code={code}
              handleNext={handleNext}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              isEnrolling={isSubmitting}
              canSelectMethod={canSelectMethod}
              onBack={() => setMethodSelected(false)}
            />
          </AuthenticationWrapper.Actions>

          <HelpLinks />
        </Stack>
      </Fade>
    </SetupPageWithoutProvider>
  );
};

export const TOTPSetupPage = () => {
  const { user, emailVerificationDetails } = useAuth();

  if (!user) {
    return <NavigateWithRedirect to="/login" />;
  }

  // The user must be verified to setup MFA
  if (!emailVerificationDetails.isEmailVerified) {
    return <NavigateWithRedirect to="/request-verification" />;
  }

  const userAlreadyHasTOTP = isUserEnrolledInMFAMethod({
    factorId: MFA_FACTOR_IDS.TOTP,
    multiFactorUser: multiFactor(user),
  });

  if (userAlreadyHasTOTP) {
    return <Navigate replace to="/dashboard" />;
  }

  return (
    <MFASetupPageContextProvider method={MFA_FACTOR_IDS.TOTP}>
      <TOTPSetupPageContent />
    </MFASetupPageContextProvider>
  );
};
